import { isAfter, isBefore, setHours, setMinutes } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { orderBy, uniq } from "lodash-es";
import { IntlShape } from "react-intl";
import { createSelector } from "reselect";

import { OrganizationSetting } from "@mapmycustomers/shared";
import EmailDraft from "@mapmycustomers/shared/types/draft/EmailDraft";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import {
  EntitiesSupportedByEmailFeature,
  EntityTypesSupportedByEmailFeature,
  Resource,
} from "@mapmycustomers/shared/types/entity";
import Setting from "@mapmycustomers/shared/types/Setting";
import parseEmailDynamicVariables from "@mapmycustomers/shared/util/email/parseEmailDynamicVariables";
import validateEmailResource from "@mapmycustomers/shared/util/email/validateEmailResource";

import { RootState } from "@app/store/rootReducer";
import EmailDraftInfo from "@app/types/email/EmailDraftInfo";
import EmailTemplateInfo from "@app/types/email/EmailTemplateInfo";
import getEmailMetadataResourcesIds from "@app/util/email/getEmailMetadataResourcesIds";
import getEmailDynamicVariablesInfo from "@app/util/fieldModel/util/getEmailDynamicVariablesInfo";
import { activityLayoutModel } from "@app/util/layout/impl";
import { parseApiDate } from "@app/util/parsers";

import doesEmailActivityLayoutHaveRequiredFields from "../../util/email/doesEmailActivityLayoutHaveRequiredFields";
import { getOrganizationSettings, getUserMetaData, getUserSettings } from "../iam";

const emailState = (state: RootState) => state.email;

export const getEmailQuota = createSelector(emailState, ({ emailQuota }) => emailQuota);

export const getEmailSyncProgress = createSelector(
  emailState,
  ({ emailSyncProgress }) => emailSyncProgress
);

export const getEmailQueue = createSelector(emailState, ({ emailQueue }) => emailQueue);

export const isEmailQueueLoading = createSelector(
  emailState,
  ({ emailQueueLoading }) => emailQueueLoading
);

export const doesNeedToOpenActivityFormAfterSendingEmail = createSelector(
  getUserMetaData,
  (metaData) => {
    const userSettingValue = metaData.openActivityFormAfterSendingEmail;
    return (
      !!userSettingValue ||
      activityLayoutModel.layouts.length > 1 ||
      doesEmailActivityLayoutHaveRequiredFields()
    );
  }
);

export const isNewEmailTemplateCreating = createSelector(
  emailState,
  ({ templateCreating }) => templateCreating
);

export const getEmailTemplates = createSelector(emailState, ({ templates }) =>
  orderBy(templates, (template) => parseApiDate(template.updatedAt), "desc")
);

export const getEmailTemplatesByEntityType = createSelector(
  [getEmailTemplates, (_, entityType: EntityTypesSupportedByEmailFeature) => entityType],
  (templates, entityType) => templates.filter((template) => template.entityType === entityType)
);

export const getEmailTemplateInfo = createSelector(
  [emailState, (_, templateId: EmailTemplate["id"]) => templateId],
  ({ templates, templatesResources }, templateId): EmailTemplateInfo | undefined => {
    const template = templates.find((template) => template.id === templateId);
    if (!template) {
      return undefined;
    }

    const templateResourceIds = getEmailMetadataResourcesIds(template);
    const { invalidResourceIds, resources } = templateResourceIds.reduce(
      (result, resourceId) => {
        const resource = templatesResources.find((resource) => resource.id === resourceId);
        if (resource) {
          result.resources.push(resource);
        }

        const { valid } = validateEmailResource(resource);
        if (!valid) {
          result.invalidResourceIds.push(resourceId);
        }

        return result;
      },
      {
        invalidResourceIds: [] as Resource["id"][],
        resources: [] as Resource[],
      }
    );

    return {
      invalidResourceIds,
      resources,
      template,
      valid: !invalidResourceIds.length,
    };
  }
);

export const getEmailDrafts = createSelector(emailState, ({ drafts }) =>
  orderBy(drafts, (draft) => parseApiDate(draft.updatedAt), "desc")
);

export const getEmailDraftsByEntityType = createSelector(
  [getEmailDrafts, (_, entityType: EntityTypesSupportedByEmailFeature) => entityType],
  (drafts, entityType) => {
    return drafts.filter((draft) => draft.metadata.recipientType === entityType);
  }
);

export const areEmailDraftsLoaded = createSelector(emailState, ({ draftsLoaded }) => draftsLoaded);

export const getEmailDraftInfo = createSelector(
  [emailState, (_, draftId: EmailDraft["id"]) => draftId, (_, __, intl: IntlShape) => intl],
  ({ drafts, draftsEntities, draftsResources }, draftId, intl): EmailDraftInfo | undefined => {
    const draft = drafts.find((draft) => draft.id === draftId);
    if (!draft) {
      return undefined;
    }

    const draftResourceIds = getEmailMetadataResourcesIds(draft);
    const { invalidResourceIds, resources } = draftResourceIds.reduce(
      (result, resourceId) => {
        const resource = draftsResources.find((resource) => resource.id === resourceId);
        if (resource) {
          result.resources.push(resource);
        }

        const { valid } = validateEmailResource(resource);
        if (!valid) {
          result.invalidResourceIds.push(resourceId);
        }

        return result;
      },
      {
        invalidResourceIds: [] as Resource["id"][],
        resources: [] as Resource[],
      }
    );

    const { entities, recipientsValid } = draft.metadata.recipients.to.reduce(
      (result, recipient) => {
        const entity = draftsEntities.find(
          (entity) => entity.entity === draft.metadata.recipientType && entity.id === recipient.id
        );
        if (entity) {
          result.entities.push(entity);
        }
        if (!entity || !entity.email) {
          result.recipientsValid = false;
        }
        return result;
      },
      { entities: [] as EntitiesSupportedByEmailFeature[], recipientsValid: true }
    );

    const [subjectDynamicVariables] = getEmailDynamicVariablesInfo({
      entities,
      entityType: draft.metadata.recipientType,
      includeOnlyParsedDynamicVariables: true,
      intl,
      parsedDynamicVariables: parseEmailDynamicVariables(draft.title),
    });

    return {
      draft,
      entities,
      invalidResourceIds,
      resources,
      subjectDynamicVariables,
      valid: !invalidResourceIds.length && recipientsValid,
    };
  }
);

export const isEmailTemplateUpdating = createSelector(
  emailState,
  ({ templateUpdating }) => templateUpdating
);

export const areEmailTemplatesLoading = createSelector(
  emailState,
  ({ templatesLoading }) => templatesLoading
);

export const isEmailTemplateDeleting = createSelector(
  emailState,
  ({ templateDeleting }) => templateDeleting
);

export const getEmailTemplatesAttachmentIds = createSelector(emailState, ({ templates }) =>
  uniq(templates.flatMap(({ attachmentIds }) => attachmentIds))
);

export const getEmailDraftsAttachmentIds = createSelector(emailState, ({ drafts }) =>
  uniq(drafts.flatMap(({ attachmentIds }) => attachmentIds))
);

export const isEmailQueueVisible = createSelector(
  emailState,
  ({ emailQueueVisible }) => emailQueueVisible
);

export const isEmailFeaturesVisible = createSelector(
  emailState,
  ({ emailFeaturesVisible }) => emailFeaturesVisible
);

export const isOrgReachEmailDayLimit = createSelector(getEmailQuota, (quota) =>
  quota ? quota.total <= quota.sent + quota.submitted : false
);

export const isEmailQuotaLoading = createSelector(
  emailState,
  ({ emailQuotaLoading }) => emailQuotaLoading
);

export const getEmailSendingWindow = createSelector(
  getUserSettings,
  (userSettings: Setting[] | undefined) => {
    const emailSetting = userSettings?.find(({ key }) => key === "bulkEmail")?.value;
    if (emailSetting) {
      return { endTime: emailSetting.endTime, startTime: emailSetting.startTime };
    }
    return undefined;
  }
);

const DAY_ORDER = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export const getEmailExcludedDayIndexes = createSelector(
  getUserSettings,
  (userSettings: Setting[] | undefined) => {
    const emailSetting = userSettings?.find(({ key }) => key === "bulkEmail")?.value;
    if (!emailSetting || !Array.isArray(emailSetting.excludedDays)) {
      return [];
    }
    return emailSetting.excludedDays
      .map((day: string) => DAY_ORDER.indexOf(day))
      .filter((index: number) => index !== -1);
  }
);

export const isOutOfEmailSendingHours = createSelector(
  getOrganizationSettings,
  getUserSettings,
  (orgSettings: Setting[] | undefined, userSettings: Setting[] | undefined) => {
    const timezone = orgSettings?.find(({ key }) => key === OrganizationSetting.TIMEZONE)?.value;
    const emailSetting = userSettings?.find(({ key }) => key === "bulkEmail")?.value;
    if (timezone && emailSetting) {
      const startTime = emailSetting.startTime.split(":");
      const endTime = emailSetting.endTime.split(":");
      const startHour = startTime[0];
      const startMinute = startTime[1] ?? 0;
      const endHour = endTime[0];
      const endMinute = endTime[1] ?? 0;
      const dateInTimeZone = toZonedTime(new Date(), timezone);

      return (
        isBefore(dateInTimeZone, setMinutes(setHours(dateInTimeZone, startHour), startMinute)) ||
        isAfter(dateInTimeZone, setMinutes(setHours(dateInTimeZone, endHour), endMinute)) ||
        // we consider then excludedDays can contain only weekend
        (Array.isArray(emailSetting.excludedDays) &&
          emailSetting.excludedDays.length &&
          [0, 6].includes(dateInTimeZone.getDay()))
      );
    }
    return false;
  }
);
